import { useState, useEffect, useRef } from "react";
import { MdError } from "react-icons/md";
import Select from "react-select";
import DefaultButton from "../buttons/defaultButton";
import { IoIosInformationCircleOutline, IoMdAdd } from "react-icons/io";
import { HiRefresh } from "react-icons/hi";
import { Tooltip } from "flowbite-react";

const Loading = () => {
  return (
    <div className="flex items-center justify-start h-full pointer-events-none">
      <p className="text-sm font-medium text-gray-400">
        Loading<span className="animate-bounce">.</span>
        <span className="animate-bounce delay-200">.</span>
        <span className="animate-bounce delay-400">.</span>
      </p>
    </div>
  );
};

const DefaultSearchSelectInput = ({
  label,
  labelClass,
  id,
  name,
  value,
  onChange,
  onBlur,
  error,
  required,
  options = [],
  isMulti = false,
  addmoreClick = null,
  onAddMore,
  onRefresh = null,
  isDisabled = false,
  customClass = null,
  className,
  info = "",
  loading = false,
  customWidth,
  disabled = false,
  lebelVertical = true,
  iconOnly = false,
  icon,
  showUsername = true,
}) => {
  const [open, setOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('below');
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 200;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('above');
      } else {
        setDropdownPosition('below');
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      calculateDropdownPosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', calculateDropdownPosition);
      window.addEventListener('scroll', calculateDropdownPosition);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', calculateDropdownPosition);
      window.removeEventListener('scroll', calculateDropdownPosition);
    };
  }, [open]);

  // ICON-ONLY MODE
  if (iconOnly) {
    const displayValue = showUsername
      ? (Array.isArray(value)
          ? value.map((v) => v.label).join(", ")
          : value?.label || null)
      : null;

    return (
      <div ref={dropdownRef} className={`relative inline-block min-w-[80px] text-lg ${className}`}>
        <button
          ref={buttonRef}
          type="button"
          className={`p-2 rounded-lg text-left min-w-[80px] ${className}`}
          onClick={() => {
            setOpen(!open);
            if (!open) {
              calculateDropdownPosition();
            }
          }}
        >
          {displayValue || icon}
        </button>

        {open && (
          <div className={`absolute z-[9999] w-64 right-0 !text-xs bg-white shadow-lg border border-gray-200 rounded-xl overflow-hidden ${
            dropdownPosition === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}>
            <Select
              menuPortalTarget={document.body}
              menuPlacement="auto"
              styles={{
                menuPortal: (base) => ({
                  ...base,
                  fontSize: "12px",
                  fontWeight: "500",
                  zIndex: 9999,
                }),
                menu: (base) => ({
                  ...base,
                  fontSize: "12px",
                  fontWeight: "500",
                  zIndex: 9999,
                  margin: 0,
                  boxShadow: "none",
                  border: "none",
                }),
                placeholder: (base) => ({
                  ...base,
                  color: "#9ca3af",
                  fontWeight: "400",
                }),
                control: (base) => ({
                  ...base,
                  border: "none",
                  boxShadow: "none",
                  minHeight: "32px",
                }),
              }}
              isDisabled={isDisabled}
              value={value}
              isMulti={isMulti}
              closeMenuOnSelect={!isMulti}
              name={name}
              disabled={disabled}
              options={
                loading
                  ? [{ value: 0, label: <Loading />, isDisabled: true }]
                  : options
              }
              className={`flex-1 react-select-container ${customWidth} ${customClass}
                               ${
                                 error
                                   ? "error !bg-red-50 rounded-lg border border-red-300"
                                   : ""
                               } ${disabled ? "bg-gray-50 text-gray-400 cursor-not-allowed" : ""}`}
              classNamePrefix="react-select"
              onChange={(selectedOption, actionMeta) => {
                if (loading && selectedOption?.value === "loading") return;
                onChange(selectedOption, actionMeta);
                if (!isMulti) {
                  setOpen(false);
                }
              }}
              onBlur={onBlur}
              menuIsOpen={true}
            />
          </div>
        )}
      </div>
    );
  }

  // FULL INPUT MODE
  return (
    <div
      className={`flex gap-1.5 w-full relative ${
        lebelVertical ? "flex-col" : "flex-row items-center gap-2"
      }`}
    >
      {label && (
        <div className="block">
          <label
            className={`text-gray-700 font-medium flex items-center text-sm ${labelClass}`}
            htmlFor={id}
          >
            <span className="flex items-center gap-[2px]">
              {label}
              {info ? (
                <div className="cursor-pointer text-gray-400 hover:text-gray-600 transition-colors">
                  <Tooltip content={info}>
                    <IoIosInformationCircleOutline />
                  </Tooltip>
                </div>
              ) : null}
            </span>
            {required ? <span className={"text-red-500 ml-0.5"}>*</span> : ""}
          </label>
        </div>
      )}
      <div className={`flex flex-col !text-sm w-full`}>
        <div className={`flex items-center gap-2`}>
          <Select
            menuPortalTarget={document.body}
            menuPlacement="auto"
            styles={{
              menuPortal: (base) => ({
                ...base,
                fontSize: "12px",
                fontWeight: "500",
                zIndex: 9999,
              }),
              placeholder: (base) => ({
                ...base,
                color: "#9ca3af",
                fontWeight: "400",
              }),
            }}
            isDisabled={isDisabled}
            isOptionDisabled={(option) => !!option.isDisabled}
            value={value}
            isMulti={isMulti ? true : false}
            closeMenuOnSelect={!isMulti}
            name={name}
            disabled={disabled}
            options={
              loading
                ? [{ value: 0, label: <Loading />, isDisabled: true }]
                : options
            }
            className={`flex-1 react-select-container border border-gray-200 rounded-lg transition-all duration-200 hover:border-gray-300 overflow-hidden ${customWidth} ${customClass}
                             ${
                               error
                                 ? "error !bg-red-50 border-red-300"
                                 : ""
                             } ${disabled ? "bg-gray-50 text-gray-400 cursor-not-allowed" : ""}`}
            classNamePrefix="react-select"
            formatOptionLabel={(option, { context }) => {
              if (context === "value") return <span>{option.label}</span>;
              return (
                <div className="flex items-center justify-between gap-2">
                  <span className={option.isDisabled ? "text-gray-300" : ""}>{option.label}</span>
                  {option.isDisabled && option.count === 0 && (
                    <span className="text-[10px] text-gray-300 font-medium whitespace-nowrap italic">No designations</span>
                  )}
                </div>
              );
            }}
            onChange={(selectedOption, actionMeta) => {
              if (loading && selectedOption?.value === "loading") return;
              onChange(selectedOption, actionMeta);
            }}
            onBlur={onBlur}
          />
          {onRefresh && (
            <button
              type="button"
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all duration-200 disabled:opacity-50"
              onClick={onRefresh}
              disabled={loading}
              title="Refresh options"
              aria-label="Refresh"
            >
              <HiRefresh className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
          {addmoreClick && addmoreClick && (
            <span
              className="text-theme font-medium text-sm cursor-pointer hover:underline"
              onClick={onAddMore}
            >
              <div className="bg-gradient-to-r from-primary-700 to-primary-500 w-10 h-10 rounded-lg text-white text-lg flexCenter shadow-sm hover:shadow-md transition-shadow">
                {<IoMdAdd />}
              </div>
            </span>
          )}
        </div>
        {error ? (
          <span className="flex items-center gap-1 text-xs text-red-500 mt-1">
            <MdError className="text-sm flex-shrink-0" />
            <p>{error}</p>
          </span>
        ) : (
          ""
        )}
      </div>
    </div>
  );
};

export default DefaultSearchSelectInput;
